import type { ISODate, ISchedulerDependency, TaskId } from '@interfaces/IScheduler';
import type { ITaskSpan } from '@apis/schedule';
import { cascade } from './cascade';
import type { CascadeFreeze } from './freeze';
import { addDays, daysBetween } from './timeScale';

// ---------------------------------------------------------------------------
// What marking a task done on a given day does to the plan
// (docs/specs/2026-09-22-done-on-the-day-amendment.md). Pure: the dialog asks
// the question, this module answers what each answer would move.
// ---------------------------------------------------------------------------

/** The day the work is marked done, against the day it was planned to end. */
export type FinishTiming = 'early' | 'onTime' | 'late';

export function finishTiming(plannedEnd: ISODate, today: ISODate): FinishTiming {
  if (today < plannedEnd) return 'early';
  return today === plannedEnd ? 'onTime' : 'late';
}

interface IFinishTask {
  id: TaskId;
  parentId?: TaskId | null;
  name: string;
  start: ISODate;
  end: ISODate;
  isMilestone: boolean;
}

/**
 * The finished task's own span (decision 2): it ends on the finish day, and a
 * finish before its planned start means it started earlier too. A milestone is
 * one date, so both ends move.
 */
export function finishedSpan(task: Pick<IFinishTask, 'start' | 'isMilestone'>, finishedOn: ISODate): { start: ISODate; end: ISODate } {
  const start = task.isMilestone || finishedOn < task.start ? finishedOn : task.start;
  return { start, end: finishedOn };
}

/** One dependent task the cascade would move. */
export interface IFinishMove {
  taskId: TaskId;
  name: string;
  from: { start: ISODate; end: ISODate };
  to: { start: ISODate; end: ISODate };
}

export interface IFinishPlan {
  /** The finished task's new span. */
  own: ITaskSpan;
  /** What the cascade would move — the finished task excluded. */
  moves: IFinishMove[];
  /** The largest move, in days either way; 0 when nothing moves. */
  maxShiftDays: number;
}

/**
 * Run the finish through the plan's own cascade (decision 3). The pre-finish
 * spans and the unchanged dependency list go in as the "previous" plan, which
 * is what turns an EARLY finish into a pull: a successor sitting exactly on
 * its constraint follows the finish back, one with slack stays. A LATE finish
 * is the ordinary push. `frozen` keeps observed work where the field put it.
 */
export function planFinish<T extends IFinishTask>(
  tasks: readonly T[],
  deps: ISchedulerDependency[],
  frozen: ReadonlyMap<TaskId, CascadeFreeze>,
  task: T,
  finishedOn: ISODate,
): IFinishPlan {
  const own = { taskId: task.id, ...finishedSpan(task, finishedOn) };
  const edited = tasks.map((t) => (t.id === task.id ? { ...t, start: own.start, end: own.end } : t));
  const next = cascade(edited, deps, [task.id], undefined, deps, frozen, tasks);
  const moves: IFinishMove[] = [];
  next.forEach((after, i) => {
    const before = tasks[i]!;
    if (after.id === task.id || (after.start === before.start && after.end === before.end)) return;
    moves.push({
      taskId: after.id,
      name: after.name,
      from: { start: before.start, end: before.end },
      to: { start: after.start, end: after.end },
    });
  });
  const shifts = moves.flatMap((m) => [daysBetween(m.from.start, m.to.start), daysBetween(m.from.end, m.to.end)]);
  return { own, moves, maxShiftDays: Math.max(0, ...shifts.map(Math.abs)) };
}

/** The spans a confirmed finish writes: its own, plus the moves if chosen. */
export function finishSpans(plan: IFinishPlan, withMoves: boolean): ITaskSpan[] {
  return [plan.own, ...(withMoves ? plan.moves.map((m) => ({ taskId: m.taskId, ...m.to })) : [])];
}

/** The finish-day field's bounds, as `finishDayBounds` sets them. */
export interface IFinishDayBounds {
  min?: ISODate;
  max: ISODate;
}

/**
 * The days the finish-day field may take. Never after today. Early: any day
 * before the planned end, floored at an observed start. Late: from the day
 * after the planned end.
 */
export function finishDayBounds(
  timing: Exclude<FinishTiming, 'onTime'>,
  task: Pick<IFinishTask, 'start' | 'end'>,
  today: ISODate,
  startObserved: boolean,
): IFinishDayBounds {
  if (timing === 'late') return { min: addDays(task.end, 1), max: today };
  return startObserved ? { min: task.start, max: today } : { max: today };
}
